import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import {
  HomePageTopVisited,
  HomePageRecentlyVisited,
  HomePageStarredEntities,
  HomePageToolkit,
  VisitDisplayProvider,
  Tool,
} from '@backstage/plugin-home';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { SearchBar } from '@backstage/plugin-search-react';
import { Content, Page, Header } from '@backstage/core-components';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { GettingStartedPanel } from './GettingStartedPanel';

const useStyles = makeStyles()((theme) => ({
  searchBar: {
    display: 'flex',
    maxWidth: '60vw',
    margin: '0 auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    '& fieldset': {
      border: 'none',
    },
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(4, 0, 3),
  },
  logo: {
    height: 80,
    width: 'auto',
  },
  searchContainer: {
    marginBottom: theme.spacing(4),
  },
  fullHeight: {
    height: '100%',
    '& > *': {
      height: '100%',
    },
  },
}));

export const HomePage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const darkFilter = theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : undefined;

  const tools: Tool[] = [
    {
      url: 'https://developer.oneadvanced.io/',
      label: 'OneAdvanced API Developer Portal',
      icon: <img src="/icons/oneadvanced.svg" alt="OneAdvanced API Developer Portal" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://ai.oneadvanced.com/',
      label: 'Intelligent Chat',
      icon: <img src="/icons/oa-intelligent-chat.png" alt="Intelligent Chat" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://advancedcsg.atlassian.net/',
      label: 'JIRA',
      icon: <img src="/icons/jira.svg" alt="JIRA" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://advancedcsg.atlassian.net/wiki',
      label: 'Confluence',
      icon: <img src="/icons/confluence.svg" alt="Confluence" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://github.com/advancedcsg/',
      label: 'GitHub - AdvancedCSG',
      icon: <img src="/icons/github.svg" alt="GitHub" style={{ width: 20, height: 20, filter: darkFilter }} />,
    },
    {
      url: 'https://advancedcsg.awsapps.com/start/#',
      label: 'AWS',
      icon: <img src="/icons/aws.svg" alt="AWS" style={{ width: 20, height: 20, filter: darkFilter }} />,
    },
    {
      url: 'https://portal.azure.com/',
      label: 'Azure',
      icon: <img src="/icons/azure.svg" alt="Azure" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://ccq.svc.oneadvanced.com/',
      label: 'SonarQube',
      icon: <img src="/icons/sonarqube.svg" alt="SonarQube" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://oneadvanced.service-now.com/esc',
      label: 'Raise a ServiceNow Ticket',
      icon: <img src="/icons/servicenow.svg" alt="ServiceNow" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://idp.oneadvanced.io/create/templates/default/grafana-cloud-request-access',
      label: 'Grafana - Access',
      icon: <img src="/icons/grafana.svg" alt="Grafana" style={{ width: 20, height: 20 }} />,
    },
    {
      url: 'https://app.fossa.com/',
      label: 'FOSSA',
      icon: <img src="/icons/fossa.svg" alt="FOSSA" style={{ width: 20, height: 20, filter: darkFilter }} />,
    },
  ];

  const handleSearchSubmit = () => {
    navigate(`/search?query=${encodeURIComponent(searchTerm)}`);
  };

  return (
    <Page themeId="home">
      <Header title="Welcome to the OneAdvanced Internal Developer Portal" />
      <Content>
        <div className={classes.logoContainer}>
          <img src="/oneadvanced-logo.svg" alt="OneAdvanced" className={classes.logo} />
        </div>
        <div className={classes.searchContainer}>
          <SearchContextProvider>
            <SearchBar
              className={classes.searchBar}
              placeholder="Search Developer Portal"
              onChange={setSearchTerm}
              onSubmit={handleSearchSubmit}
              InputProps={{
                style: { boxShadow: 'none' },
                disableUnderline: true,
              }}
            />
          </SearchContextProvider>
        </div>
        <GettingStartedPanel />
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <div className={classes.fullHeight}>
              <HomePageToolkit
                tools={tools}
                title="Links"
              />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.fullHeight}>
              <VisitDisplayProvider>
                <HomePageTopVisited numVisitsTotal={5} numVisitsOpen={5} />
              </VisitDisplayProvider>
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.fullHeight}>
              <VisitDisplayProvider>
                <HomePageRecentlyVisited numVisitsTotal={5} numVisitsOpen={5} />
              </VisitDisplayProvider>
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.fullHeight}>
              <HomePageStarredEntities />
            </div>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
